import React, { useEffect, useState } from 'react';

import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  LockOutlined,
  MoreOutlined,
  PlusOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Dropdown,
  GetProp,
  MenuProps,
  Modal,
  Row,
  Table,
  TablePaginationConfig,
  TableProps,
  Tag,
  message,
} from 'antd';
import Search from 'antd/es/input/Search';
import Column from 'antd/es/table/Column';
import { SearchProps } from 'antd/lib/input';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Layout from '../../../components/Layout/Layout';
import appI18n from '../../../config/i18n';
import { ApiErrorData } from '../../../models/ApiResponse';
import { Country } from '../../../models/Country';
import { DashboardNew } from '../../../models/DashboardNew';
import { CountryService } from '../../../services/CountryService';
import { DashboardNewService } from '../../../services/DashboardNewService';
import { FunctionsHelper } from '../../../utils/FunctionsHelper';
import DetailDashboardNewModal from './Components/DetailDashboardNewModal';
import NewDashboardNewModal from './Components/NewDashboardNewModal';

import './News.scss';

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Parameters<GetProp<TableProps, 'onChange'>>[1];
}

const PAGE_SIZE = 10;

function News() {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const dateFormat: string = useSelector((state: any) => state.intl.dateFormat);
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, contextModalHolder] = Modal.useModal();
  const [tableLoading, setTableLoading] = useState(true);
  const [dataSource, setDataSource] = useState<DashboardNew[]>([]);
  const [filteredDataSource, setFilteredDataSource] = useState<DashboardNew[]>(
    []
  );
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: PAGE_SIZE,
    },
  });
  const [countries, setCountries] = useState<Country[]>([]);

  const [selectedRow, setSelectedRow] = useState<DashboardNew | undefined>(
    undefined
  );
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isNewResourceModalOpen, setIsNewResourceModalOpen] = useState(false);

  /* Filters */
  const [term, setTerm] = useState('');

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    fetchData(tableParams.pagination?.current);
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams.sortField,
    tableParams.sortOrder,
  ]);

  useEffect(() => {
    fetchData(1);
  }, [term]);

  const init = async () => {
    setTableLoading(true);

    const [countryResponse] = await Promise.all([
      CountryService.getAll(),
      fetchDataFromServer(false),
    ]);

    if (countryResponse.success) {
      setCountries(countryResponse.data as Country[]);
    } else {
      const error = countryResponse.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.news.scene.errors.fetch_countries'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const onSearch: SearchProps['onSearch'] = (value) => {
    setTerm(value);
  };

  const createResourceModal = () => {
    setIsNewResourceModalOpen(true);
  };

  const showDetailResource = async (row: DashboardNew) => {
    setSelectedRow(row);
    setIsDetailModalOpen(true);
  };

  const handleCloseNewResourceModal = () => {
    setIsNewResourceModalOpen(false);
  };

  const handleCloseShowDetailResourceModal = () => {
    setSelectedRow(undefined);
    setIsDetailModalOpen(false);
  };

  const deleteNew = async (row: DashboardNew) => {
    modal.confirm({
      title: t('scenes.settings.news.scene.delete_confirm.title'),
      icon: <ExclamationCircleOutlined />,
      content: t('scenes.settings.news.scene.delete_confirm.content', {
        id: row.id,
      }),
      okText: 'SI',
      cancelText: 'NO',
      onOk: async () => {
        setTableLoading(true);
        const response = await DashboardNewService.delete(row.id);

        if (response.success) {
          await fetchDataFromServer();
          messageApi.success(
            t('scenes.settings.news.scene.messages.delete_success', {
              id: row.id,
            })
          );
        } else {
          const error = response.data as ApiErrorData;
          messageApi.error(
            (error.message as string) ||
              t('scenes.settings.news.scene.errors.delete_failed'),
            3.5
          );
        }

        setTableLoading(false);
      },
    });
  };

  const getDropdownMenu = (row: DashboardNew): MenuProps['items'] => {
    const menus: MenuProps['items'] = [];
    let itemNro = 1;

    menus.push({
      key: `${itemNro++}`,
      label: (
        <span>
          <EyeOutlined style={{ marginRight: '8px' }} />{' '}
          {t('scenes.settings.news.scene.dropdown.view_detail')}
        </span>
      ),
      onClick: () => {
        showDetailResource(row);
      },
    });

    if (!row.isActive) {
      menus.push({
        key: `${itemNro++}`,
        label: (
          <span>
            <UnlockOutlined style={{ marginRight: '8px' }} />{' '}
            {t('scenes.settings.news.scene.dropdown.activate')}
          </span>
        ),
        onClick: async () => {
          await setAsActive(row);
        },
      });
    } else {
      menus.push({
        key: `${itemNro++}`,
        label: (
          <span>
            <LockOutlined style={{ marginRight: '8px' }} />{' '}
            {t('scenes.settings.news.scene.dropdown.deactivate')}
          </span>
        ),
        onClick: async () => {
          await setAsInactive(row);
        },
      });
    }

    menus.push({
      key: `${itemNro++}`,
      label: (
        <span>
          <DeleteOutlined style={{ marginRight: '8px' }} />{' '}
          {t('scenes.settings.news.scene.dropdown.delete')}
        </span>
      ),
      danger: true,
      onClick: () => {
        deleteNew(row);
      },
    });

    return menus;
  };

  const setAsActive = async (dashboardNew: DashboardNew) => {
    setTableLoading(true);
    const response = await DashboardNewService.setAsActive(dashboardNew.id);

    if (response.success) {
      await fetchDataFromServer();
      messageApi.success(
        t('scenes.settings.news.scene.messages.activate_success', {
          id: dashboardNew.id,
        })
      );
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.news.scene.errors.activate_failed'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const setAsInactive = async (dashboardNew: DashboardNew) => {
    setTableLoading(true);
    const response = await DashboardNewService.setAsInactive(dashboardNew.id);

    if (response.success) {
      await fetchDataFromServer();
      messageApi.success(
        t('scenes.settings.news.scene.messages.deactivate_success', {
          id: dashboardNew.id,
        })
      );
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.news.scene.errors.deactivate_failed'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const handleTableChange: TableProps<DashboardNew>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    const sorterLocal = sorter as any;

    setTableParams({
      pagination,
      filters,
      sortOrder:
        sorterLocal.order && sorterLocal.columnKey
          ? sorterLocal.order
          : undefined,
      sortField:
        sorterLocal.order && sorterLocal.columnKey
          ? sorterLocal.columnKey
          : undefined,
    });

    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setFilteredDataSource([]);
    }
  };

  const fetchDataFromServer = async (withLoading = false) => {
    if (withLoading) {
      setTableLoading(true);
    }
    const response = await DashboardNewService.getAll();

    if (response.success) {
      setDataSource(response.data as DashboardNew[]);
      fetchData(1, response.data as DashboardNew[]);
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.news.scene.errors.fetch_news'),
        3.5
      );
    }

    if (withLoading) {
      setTableLoading(false);
    }
  };

  const fetchData = (currentPage = 1, data?: DashboardNew[]) => {
    const termFilter = FunctionsHelper.normalizeText(term.trim());

    let filteredData = data !== undefined ? data : [...dataSource];

    filteredData = filteredData.sort((a, b) => {
      return b.id - a.id;
    });

    if (termFilter.length > 0) {
      filteredData = filteredData.filter((record) => {
        return FunctionsHelper.normalizeText(`${record.title}`).includes(
          termFilter
        );
      });
    }

    const pageSize = tableParams.pagination?.pageSize || PAGE_SIZE;
    const total = filteredData.length;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    setFilteredDataSource(paginatedData);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: currentPage,
        total: total,
      },
    });
  };

  const getCountryName = (countryCode: string) => {
    const country = countries.find((record) => {
      return record.code === countryCode;
    });

    return country ? country.name : countryCode;
  };

  const createNewDashboardNew = async (
    countryCode: string,
    title: string,
    content: string,
    logoUrl: string,
    publishedDate: string,
    expirationDate: string | null
  ) => {
    setTableLoading(true);

    const response = await DashboardNewService.create({
      countryCode,
      title,
      content,
      logoUrl,
      publishedDate,
      expirationDate: expirationDate ? expirationDate : undefined,
    });

    if (response.success) {
      await fetchDataFromServer(true);
      messageApi.success(
        t('scenes.settings.news.scene.messages.create_success', {
          id: (response.data as { createdId: number }).createdId,
        })
      );
      handleCloseNewResourceModal();
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.news.scene.errors.create_failed'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const updateNewDashboardNew = async (
    id: number,
    title: string,
    content: string,
    logoUrl: string,
    publishedDate: string,
    expirationDate: string | null
  ) => {
    setTableLoading(true);

    const response = await DashboardNewService.update(id, {
      title,
      content,
      logoUrl,
      publishedDate,
      expirationDate: expirationDate ? expirationDate : undefined,
    });

    if (response.success) {
      await fetchDataFromServer(true);
      messageApi.success(
        t('scenes.settings.news.scene.messages.update_success', { id })
      );
      handleCloseShowDetailResourceModal();
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.news.scene.errors.update_failed'),
        3.5
      );
    }

    setTableLoading(false);
  };

  return (
    <Layout title={''}>
      {contextHolder}
      {contextModalHolder}
      <Card>
        <div className="filter-container">
          <Row gutter={[16, 12]} align="middle">
            <Col flex="none">
              <label className="filter-search-container">
                {t('scenes.settings.news.scene.filters.label')}{' '}
                <Search
                  placeholder={t(
                    'scenes.settings.news.scene.filters.search_placeholder_title'
                  )}
                  onSearch={onSearch}
                  disabled={tableLoading}
                  allowClear
                />
              </label>
            </Col>
            <Col flex="auto" />
            <Col flex="none" className="filter-buttons-container">
              <Button
                type="primary"
                onClick={createResourceModal}
                disabled={tableLoading}
              >
                <PlusOutlined />{' '}
                {t('scenes.settings.news.scene.buttons.create_news')}
              </Button>
            </Col>
          </Row>
        </div>
      </Card>

      <Card style={{ marginTop: '12px' }}>
        <Table<DashboardNew>
          dataSource={filteredDataSource}
          bordered
          loading={tableLoading}
          size="small"
          scroll={{ x: 1300 }}
          pagination={tableParams.pagination}
          rowKey={(record) => record.id}
          onChange={handleTableChange}
        >
          <Column
            title={t('scenes.settings.news.scene.table.columns.id')}
            dataIndex="id"
            key="id"
            width={60}
            align="center"
          />

          <Column
            title={t('scenes.settings.news.scene.table.columns.title')}
            dataIndex="title"
            key="name"
            ellipsis={true}
          />

          <Column
            width={140}
            align="center"
            title={t('scenes.settings.news.scene.table.columns.country')}
            key="country"
            render={(row: DashboardNew) => (
              <span>{getCountryName(row.countryCode)}</span>
            )}
          />

          <Column
            width={180}
            align="center"
            title={t('scenes.settings.news.scene.table.columns.published_at')}
            key="publishedAt"
            render={(row: DashboardNew) => (
              <span>{dayjs(row.publishedDate).format(dateFormat)}</span>
            )}
          />

          <Column
            width={180}
            align="center"
            title={t('scenes.settings.news.scene.table.columns.expired_at')}
            key="expiredAt"
            render={(row: DashboardNew) => (
              <span>
                {row.expirationDate
                  ? dayjs(row.expirationDate).format(dateFormat)
                  : ''}
              </span>
            )}
          />

          <Column
            width={120}
            align="center"
            title={t('scenes.settings.news.scene.table.columns.status')}
            key="status"
            render={(row: DashboardNew) => (
              <>
                {row.isActive ? (
                  <Tag color="processing">
                    {t('scenes.settings.news.scene.table.status.active')}
                  </Tag>
                ) : (
                  <Tag color="error">
                    {t('scenes.settings.news.scene.table.status.inactive')}
                  </Tag>
                )}
              </>
            )}
          />

          <Column
            width={120}
            align="center"
            title=""
            key="actions"
            render={(row: DashboardNew) => (
              <Dropdown
                menu={{ items: getDropdownMenu(row) }}
                placement="bottomLeft"
                trigger={['click']}
              >
                <Button size="small">
                  <MoreOutlined />
                </Button>
              </Dropdown>
            )}
          />
        </Table>
      </Card>

      {/* Modals */}
      <NewDashboardNewModal
        isOpen={isNewResourceModalOpen}
        handleModalCancel={handleCloseNewResourceModal}
        submit={createNewDashboardNew}
        countries={countries}
      />

      {selectedRow && (
        <DetailDashboardNewModal
          id={selectedRow.id}
          isOpen={isDetailModalOpen}
          handleModalCancel={handleCloseShowDetailResourceModal}
          submit={updateNewDashboardNew}
          countries={countries}
        />
      )}
    </Layout>
  );
}

export default News;
